import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { FaAppStoreIos, FaGooglePlay } from 'react-icons/fa';
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiExternalLink, FiMessageCircle, FiPercent, FiSearch, FiX } from 'react-icons/fi';
import { useInView } from 'react-intersection-observer';
import { SectionHeader } from '../ui/SectionHeader';

interface Service {
  id: string;
  icon: React.ReactNode;
  title: string;
  tagline: string;
  description: string;
  features: string[];
  images: { src: string; title?: string }[];
  stats?: { label: string; value: string }[];
}

export const ServicesSection: React.FC = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const services: Service[] = [
    {
      id: 'database',
      icon: <FiSearch size={28} />,
      title: 'استكشف +2000 برنامج تعليمي',
      tagline: 'كل التخصصات الجامعية والجامعات في مكان واحد',
      description: 'تصفح قاعدة بيانات شاملة لجميع التخصصات في الجامعات السعودية. ابحث بذكاء، قارن بسهولة، واعثر على التخصص المثالي لك.',
      features: [
        'بحث ذكي بالكلمات المفتاحية',
        'فلترة حسب الجامعة والمدينة',
        'معلومات شاملة عن كل تخصص',
        'شروط القبول وفرص العمل'
      ],
      images: [
        { src: '/major_explorer_search-min.PNG', title: '' },
        { src: '/major_explorer_filter-min.PNG', title: '' },
        { src: '/major_explorer_overview-min.PNG', title: '' },
        { src: '/major_explorer_admission-min.PNG', title: '' },
        { src: '/major_explorer_admission_requirements-min.PNG', title: '' },
        { src: '/major_explorer_univerisites-min.PNG', title: '' }
      ],
      stats: [
        { label: 'تخصص', value: '230+' },
        { label: 'جامعة', value: '30+' },
        { label: 'بحث يومي', value: '5.5k+' }
      ]
    },
    {
      id: 'ask-grad',
      icon: <img src="/icons/dialog.svg" alt="أيقونة اسأل خريج - استشارات مهنية" className="w-7 h-7" />,
      title: 'اسأل خريج',
      tagline: 'جلسات استشارية مع خريجين يعملون في سوق العمل',
      description: 'احجز جلسات مباشرة مع خريجين يعملون في تخصصك المستهدف. اسأل عن الواقع العملي، التحديات، والفرص المتاحة.',
      features: [
        'جلسات فردية أو جماعية',
        'خريجون من أبرز التخصصات',
        'مواعيد مرنة تناسبك',
        'نصائح عملية وصادقة'
      ],
      images: [
        { src: '/ask_grad_session_type.jpeg', title: 'اختر نوع الجلسة' },
        { src: '/ask_grad_example_majors-min.PNG', title: 'اختر التخصص' },
        { src: '/ask_grad_group_session-min.PNG', title: 'تعرف على المستشار والمواعيد' },
        { src: '/ask_grad_secure_payments-min.PNG', title: 'دفع آمن' },
        { src: '/ask_grad_easy_scheduling-min.PNG', title: 'حجز سهل ومرن' },
        { src: '/ask_grad_counselors-min.PNG', title: 'مستشارين متميزين' },
        { src: '/ask_grad_consultation_reviews-min.PNG', title: 'طلاب مستفيدين' },
        { src: '/ask_grad_consultation_reviews_extended-min.PNG', title: 'طلاب مستفيدين' },
      ],
      stats: [
        { label: 'مستشار متاح', value: '40+' },
        { label: 'جلسة شهرياً', value: '1000+' },
        { label: 'تقييم', value: '4.93/5' }
      ]
    },
    {
      id: 'comparison',
      icon: <img src="/icons/sortHorizontal.svg" alt="أيقونة مقارنة التخصصات" className="w-7 h-7" />,
      title: 'قارن بين التخصصات',
      tagline: 'قرارات مدروسة بناءً على بيانات حقيقية',
      description: 'قارن بين أي تخصصين جنباً إلى جنب. شاهد الفروقات في الدراسة، سوق العمل، والرواتب.',
      features: [
        'مقارنة شاملة ومختصرة بين الدراسة والوظيفة',
        'نسبة التشابه بين التخصصات',
        'احفظ المقارنات للرجوع إليها',
        'بيانات من مصادر متنوعة وموثوقة'
        
      ],
      images: [
        { src: '/comparison_select_majors-min.PNG', title: '' },
        { src: '/comparison_overview-min.PNG', title: '' },
        { src: '/comparison_career-min.PNG', title: '' },
        { src: '/comparison_similairty-min.PNG', title: '' },
        { src: '/comparison_previous-min.PNG', title: '' }
      ]
    },
    {
      id: 'chatbot',
      icon: <FiMessageCircle size={28} />,
      title: 'المرشد الذكي',
      tagline: 'يعرف اسئلتك قبل ما تسأل',
      description: 'مساعد ذكي مطور بالذكاء الاصطناعي يفهم احتياجاتك ويجيب على جميع استفساراتك حول التخصصات في أي وقت.',
      features: [
        'متاح على مدار الساعة',
        'يفهم السياق السعودي',
        'إجابات دقيقة ومحدثة',
'أحدث نماذج الذكاء الاصطناعي'
      ],
      images: [
        { src: '/chatbot_study_questions-min.PNG', title: '' },
        { src: '/chatbot_study_questions_opened-min.PNG', title: '' },
        { src: '/chatbot_study_question_answer-min.PNG', title: '' },
        { src: '/chatbot_career_questions-min.PNG', title: '' }
      ]
    },
    {
      id: 'calculator',
      icon: <FiPercent size={28} />,
      title: 'احسب نسبتك الموزونة',
      tagline: 'اعرف فرصك في القبول بدقة',
      description: 'احسب نسبتك الموزونة لجميع الجامعات والتخصصات بضغطة واحدة. لا حاجة لزيارة مواقع متعددة.',
      features: [
        'جميع الجامعات السعودية',
        'حساب دقيق وفوري',
        'احفظ نتائجك',

      ],
      images: [
        { src: '/calculator_example-min.PNG', title: '' },
        { src: '/calculator_previous-min.PNG', title: '' },
        { src: '/calculator_step-min.PNG', title: '' },
      ],
      stats: [
        { label: 'حساب شهري', value: '140K+' },
        { label: 'دقة', value: '100%' }
      ]
    }
  ];

  const openImageModal = (serviceIndex: number, imageIndex: number) => {
    setCurrentServiceIndex(serviceIndex);
    setCurrentImageIndex(imageIndex);
    setImageModalOpen(true);
  };

  const currentService = services[currentServiceIndex];
  const currentServiceImages = currentService?.images || [];

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % currentServiceImages.length);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + currentServiceImages.length) % currentServiceImages.length);
  };

  const toggleService = (serviceId: string) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section id="services" className="py-12 md:py-16 lg:py-20 bg-gray-50">
      <div className="section-container px-4 sm:px-6 lg:px-8">
        <SectionHeader
          subtitle="ماذا نقدم؟"
          title="كل ما تحتاجه في مكان واحد"
          description="أدوات ذكية تساعدك على اتخاذ أفضل قرار لمستقبلك"
        />

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="mt-8 md:mt-16"
        >
          {/* Services Grid */}
          <div className="grid gap-4 md:gap-6">
            {services.map((service) => (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                {/* Service Header - Always Visible */}
                <div 
                  className="p-4 sm:p-6 md:p-8 cursor-pointer"
                  onClick={() => toggleService(service.id)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-start gap-3 sm:gap-4">
                      {/* Icon */}
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-brand-primary bg-opacity-10 rounded-lg flex items-center justify-center text-brand-primary flex-shrink-0">
                        {service.icon}
                      </div>
                      
                      {/* Title & Tagline */}
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-government-navy mb-1 font-heading">
                          {service.title}
                        </h3>
                        <p className="text-xs sm:text-sm md:text-base text-gray-600">
                          {service.tagline}
                        </p>
                      </div>
                    </div>
                    
                    {/* Expand Button */}
                    <button className="flex items-center gap-1 sm:gap-2 text-brand-primary font-medium self-start sm:self-auto ml-13 sm:ml-0">
                      <span className="text-xs sm:text-sm md:text-base">{expandedService === service.id ? 'إخفاء' : 'المزيد'}</span>
                      {expandedService === service.id ? <FiChevronUp size={18} className="sm:w-5 sm:h-5" /> : <FiChevronDown size={18} className="sm:w-5 sm:h-5" />}
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                <AnimatePresence>
                  {expandedService === service.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <div className="border-t border-gray-100">
                        <div className="p-4 sm:p-6 md:p-8">
                          {/* Mobile/Tablet Layout - Optimized for Phone Screenshots */}
                          <div className="block lg:hidden space-y-6">
                            {/* Content First on Mobile */}
                            <div>
                              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                                {service.description}
                              </p>
                              
                              <div className="space-y-2 sm:space-y-3">
                                {service.features.map((feature, index) => (
                                  <div key={index} className="flex items-start gap-2 sm:gap-3">
                                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-600 rounded-full"></div>
                                    </div>
                                    <span className="text-gray-700 text-xs sm:text-sm">{feature}</span>
                                  </div>
                                ))}
                              </div>

                              {/* Stats */}
                              {service.stats && (
                                <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 sm:mt-6">
                                  {service.stats.map((stat, index) => (
                                    <div key={index} className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl">
                                      <div className="text-base sm:text-lg font-bold text-brand-primary">
                                        {stat.value}
                                      </div>
                                      <div className="text-[9px] sm:text-xs text-gray-600">
                                        {stat.label}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Mobile Screenshots - Horizontal Scroll */}
                            <div>
                              <h5 className="text-sm font-semibold text-government-navy mb-3 text-center">لقطات من التطبيق</h5>
                              <div className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
                                {service.images.map((image, index) => (
                                  <div key={index} className="flex-shrink-0 snap-center">
                                    <div className="w-40 sm:w-48 space-y-2">
                                      {image.title && (
                                        <h6 className="text-xs font-medium text-government-navy text-center line-clamp-1">
                                          {image.title}
                                        </h6>
                                      )}
                                      <div 
                                        className="relative rounded-2xl overflow-hidden shadow-lg bg-gray-900 p-1 cursor-pointer hover:scale-105 transition-transform duration-300"
                                        onClick={() => openImageModal(services.findIndex(s => s.id === service.id), index)}
                                      >
                                        <img 
                                          src={image.src} 
                                          alt={`لقطة شاشة من تطبيق ضوء الخريج - ${service.title}${image.title ? ` - ${image.title}` : ` ${index + 1}`}`}
                                          className="w-full aspect-[430/851] object-cover rounded-xl"
                                          loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 rounded-xl transition-all duration-300 flex items-center justify-center">
                                          <div className="w-8 h-8 bg-white bg-opacity-80 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                                            <FiExternalLink size={16} className="text-brand-primary" />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Desktop Layout - Side by Side with Phone Mockups */}
                          <div className="hidden lg:grid lg:grid-cols-5 gap-8">
                            {/* Left: Description & Features (3 columns) */}
                            <div className="col-span-3">
                              <p className="text-base text-gray-700 leading-relaxed mb-6">
                                {service.description}
                              </p>
                              
                              <div className="space-y-3">
                                {service.features.map((feature, index) => (
                                  <div key={index} className="flex items-start gap-3">
                                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                    </div>
                                    <span className="text-gray-700 text-base">{feature}</span>
                                  </div>
                                ))}
                              </div>

                              {/* Stats */}
                              {service.stats && (
                                <div className="grid grid-cols-3 gap-3 mt-6">
                                  {service.stats.map((stat, index) => (
                                    <div key={index} className="text-center p-3 bg-gray-50 rounded-xl">
                                      <div className="text-2xl font-bold text-brand-primary mb-1">
                                        {stat.value}
                                      </div>
                                      <div className="text-sm text-gray-600">
                                        {stat.label}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Right: Phone Screenshots (2 columns) */}
                            <div className="col-span-2">
                              <h5 className="text-lg font-semibold text-government-navy mb-4 text-center">لقطات من التطبيق</h5>
                              <div className={`${service.images.length > 4 ? 'grid grid-cols-2 gap-3' : 'flex gap-4 justify-center'}`}>
                                {service.images.map((image, index) => (
                                  <div key={index} className="space-y-2 flex flex-col items-center">
                                    {image.title && (
                                      <h6 className="text-xs font-medium text-government-navy text-center line-clamp-2 min-h-[32px] flex items-center">
                                        {image.title}
                                      </h6>
                                    )}
                                    <div 
                                      className="relative group cursor-pointer"
                                      onClick={() => openImageModal(services.findIndex(s => s.id === service.id), index)}
                                    >
                                      {/* Phone Frame - Thinner */}
                                      <div className="bg-gray-900 rounded-2xl p-1 shadow-2xl">
                                        <img 
                                          src={image.src} 
                                          alt={`لقطة شاشة من تطبيق ضوء الخريج - ${service.title}${image.title ? ` - ${image.title}` : ` ${index + 1}`}`}
                                          className={`w-full ${service.images.length > 4 ? 'max-w-[140px]' : 'max-w-[180px]'} aspect-[430/851] object-cover rounded-xl group-hover:scale-105 transition-transform duration-300`}
                                          loading="lazy"
                                        />
                                      </div>
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-2xl transition-all duration-300 flex items-center justify-center">
                                        <div className="w-8 h-8 bg-white bg-opacity-80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                          <FiExternalLink size={16} className="text-brand-primary" />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-8 sm:mt-12 md:mt-16 lg:mt-20 text-center"
          >
            <div className="bg-brand-primary rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16">
              <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 font-heading">
                ابدأ رحلتك التعليمية اليوم
              </h3>
              <p className="text-sm sm:text-base md:text-lg text-gray-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
                انضم لأكثر من نصف مليون طالب يستخدمون ضوء الخريج
              </p>
              
              {/* Download Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center items-center max-w-2xl mx-auto">
                <a 
                  href="https://apps.apple.com/us/app/%D8%B6%D9%88%D8%A1-%D8%A7%D9%84%D8%AE%D8%B1-%D9%8A%D8%AC/id1109148279"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black hover:bg-gray-900 text-white px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-105 shadow-xl w-full sm:w-auto justify-center min-w-0"
                >
                  <FaAppStoreIos size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
                  <div className="text-right">
                    <div className="text-[9px] sm:text-[10px] opacity-90">حمّل من</div>
                    <div className="text-xs sm:text-sm md:text-base font-semibold">App Store</div>
                  </div>
                </a>
                
                <a 
                  href="https://play.google.com/store/apps/details?id=sarah.com.graduateguide&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black hover:bg-gray-900 text-white px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-105 shadow-xl w-full sm:w-auto justify-center min-w-0"
                >
                  <FaGooglePlay size={16} className="sm:w-4 sm:h-4 flex-shrink-0" />
                  <div className="text-right">
                    <div className="text-[9px] sm:text-[10px] opacity-90">حمّل من</div>
                    <div className="text-xs sm:text-sm md:text-base font-semibold">Google Play</div>
                  </div>
                </a>

                <a 
                  href="https://alkhreej.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white hover:bg-gray-100 text-brand-primary px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-105 shadow-xl w-full sm:w-auto justify-center border-2 border-white/20 min-w-0"
                >
                  <FiExternalLink size={16} className="sm:w-4 sm:h-4 flex-shrink-0" />
                  <div className="text-center">
                    <div className="text-[9px] sm:text-[10px] opacity-70">استخدم على</div>
                    <div className="text-xs sm:text-sm md:text-base font-semibold">الويب</div>
                  </div>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Coming Soon Features */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-8 sm:mt-12 md:mt-16"
          >
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-government-navy text-center mb-6 sm:mb-8 font-heading">
              قريباً في ضوء الخريج
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {[
                { title: 'اختبار الشخصية والميول المهنية', description: 'تقييم شامل لتحديد التخصص المناسب' },
                { title: 'برنامج التظليل المهني', description: 'تجربة عملية في بيئة العمل الحقيقية' },
                { title: 'جلسات مستشار مهني معتمد', description: 'استشارات متخصصة من خبراء مرخصين' }
              ].map((feature, index) => (
                <div key={index} className="relative">
                  <div className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-brand-secondary text-government-navy-bold text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                    قريباً
                  </div>
                  <div className="card-government">
                    <h4 className="font-bold text-government-navy mb-2 mt-3 sm:mt-4 font-heading text-sm sm:text-base">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {imageModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
            onClick={() => setImageModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setImageModalOpen(false)}
                className="absolute -top-12 right-0 z-10 w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white transition-all duration-300"
              >
                <FiX size={24} />
              </button>

              {/* Navigation Buttons */}
              {currentServiceImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white hover:bg-opacity-30 rounded-full flex items-center justify-center text-black transition-all duration-300"
                  >
                    <FiChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white  hover:bg-opacity-30 rounded-full flex items-center justify-center text-black transition-all duration-300"
                  >
                    <FiChevronRight size={24} />
                  </button>
                </>
              )}

              {/* Image */}
              <div className="bg-gray-900 rounded-3xl p-3 shadow-2xl">
                <img
                  src={currentServiceImages[currentImageIndex]?.src}
                  alt={`لقطة شاشة من تطبيق ضوء الخريج - ${currentService?.title}${currentServiceImages[currentImageIndex]?.title ? ` - ${currentServiceImages[currentImageIndex].title}` : ` ${currentImageIndex + 1}`}`}
                  className="w-full aspect-[430/851] object-cover rounded-2xl"
                />
              </div>

              {/* Image Info */}
              <div className="mt-4 text-center">
                {currentServiceImages[currentImageIndex]?.title && (
                  <h3 className="text-white text-lg font-semibold mb-1">
                    {currentServiceImages[currentImageIndex].title}
                  </h3>
                )}
                <p className="text-gray-300 text-sm">
                  {currentService?.title}
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  {currentImageIndex + 1} من {currentServiceImages.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};